import Head from 'next/head'
import { Element } from 'react-scroll'

import TopicNewestList from 'app/PelatihanMandiriPage/partials/Topic/TopicNewestList'
import TopicRecommendationList from 'app/PelatihanMandiriPage/partials/Topic/TopicRecommendationList'
import TopicProgress from 'app/PelatihanMandiriPage/partials/Topic/TopicProgress'
import TopicWebinar from 'app/PelatihanMandiriPage/partials/Topic/Webinar'
import TopicMore from 'app/PelatihanMandiriPage/partials/Topic/TopicMore'
import TabbedNav from '../TabbedNav'
import {
  useTopicNewest,
  useTopicRecommendation,
  useTopicProgress,
} from 'api/microlearning/useAPITopics'
import { useSETrackerEvent } from 'app/PelatihanMandiriPage/hooks/useSETrackEvent'
import useTopicSurveyBanner from 'app/PelatihanMandiriPage/hooks/useTopicSurveyBanner'
import { MILKvKeys, useGetKeyPairValue } from 'api/microlearning/useAPIKeyValue'
import DesktopLandingShell from 'app/PelatihanMandiriPage/components/DesktopLandingShell'
import { usePelatihanMandiriDesktopView } from 'app/PelatihanMandiriPage/hooks/usePelatihanMandiriDesktopView'

type forYouProps = {
  isDesktop: boolean
}

const SHOW_PROGRES_TOPIK = false

const ForYouRevamp = ({ isDesktop }: forYouProps) => {
  const { SETrackTopNav } = useSETrackerEvent()
  const isPelatihanMandiriDesktopViewEnabled = usePelatihanMandiriDesktopView()
  const shouldUseDesktopView = isDesktop && isPelatihanMandiriDesktopViewEnabled

  const topicSurveyBanner = useTopicSurveyBanner()
  const getTopicNewest = useTopicNewest()
  const getTopicRecommendation = useTopicRecommendation()
  const getTopicProgress = useTopicProgress(SHOW_PROGRES_TOPIK)
  const getWebinar = useGetKeyPairValue(MILKvKeys.WebinarEntries)

  const handleScrollClicked = ({ label }) => {
    SETrackTopNav({
      action_type: 'click_top_nav',
      action_value: label,
    })
  }

  /* FOR PROGRESS TOPIC */
  // first we sorting percentage progress highest to lowest
  const sortedProgress = getTopicProgress?.data?.data?.sort(
    (a, b) => b.percentage - a.percentage
  )

  // we get the first progress that has percentage 100 and aksi nyata state is NOT_CREATED, NEED_REVISION, CONFIRMED, CREATED
  const selectedProgress =
    sortedProgress?.find(
      (item) =>
        item.percentage === 100 &&
        ['NOT_CREATED', 'CONFIRMED', 'CREATED', 'NEED_REVISION'].includes(
          item.aksiNyata.state
        )
    ) ||
    sortedProgress?.find(
      (item) =>
        item.percentage < 100 &&
        item.percentage > 1 &&
        item.modules.find(
          (module) =>
            module.postTest === false && module.prerequisiteCompleted === true
        )
    )

  const content = (
    <div className={shouldUseDesktopView ? 'pb-5' : 'px-3 pb-5 pt-40'}>
      {topicSurveyBanner(getTopicProgress?.data?.data, {
        className: shouldUseDesktopView ? 'mb-6' : 'my-6 first:mt-0',
      })}

      <div
        data-testid={
          shouldUseDesktopView ? 'pelatihan-mandiri-desktop-grid' : undefined
        }
        className={
          shouldUseDesktopView ? 'grid grid-cols-1 gap-6 xl:grid-cols-2' : ''
        }
      >
        <div
          className={
            shouldUseDesktopView ? 'rounded-xl bg-white p-4 shadow-sm' : 'pb-6'
          }
        >
          <TopicNewestList
            isLoading={getTopicNewest.isLoading}
            items={getTopicNewest?.data?.data || []}
            gridClassName={
              shouldUseDesktopView ? 'lg:grid-cols-2 xl:grid-cols-1' : ''
            }
          />
        </div>
        <Element
          name="rekomendasi-belajar"
          className={
            shouldUseDesktopView ? 'rounded-xl bg-white p-4 shadow-sm' : 'pb-6'
          }
        >
          <TopicRecommendationList
            isLoading={getTopicRecommendation.isLoading}
            items={getTopicRecommendation?.data?.data || []}
            isError={getTopicRecommendation.isError}
            gridClassName={
              shouldUseDesktopView ? 'lg:grid-cols-2 xl:grid-cols-1' : ''
            }
          />
        </Element>
        {SHOW_PROGRES_TOPIK && (
          <Element
            name="progress-topik"
            className={
              shouldUseDesktopView
                ? 'rounded-xl bg-white p-4 shadow-sm'
                : 'pb-6'
            }
          >
            <TopicProgress
              isLoading={getTopicProgress.isLoading}
              selectedProgress={selectedProgress}
              isError={getTopicProgress.isError}
            />
          </Element>
        )}
        <Element
          name="webinar"
          className={
            shouldUseDesktopView ? 'rounded-xl bg-white p-4 shadow-sm' : 'pb-6'
          }
        >
          <TopicWebinar
            items={getWebinar?.data?.data?.webinar || []}
            isLoading={getWebinar.isLoading}
            isError={getWebinar.isError}
            itemWrapperClassName={
              shouldUseDesktopView ? 'grid grid-cols-1 gap-4' : ''
            }
            itemClassName={shouldUseDesktopView ? '' : undefined}
          />
        </Element>
        <div
          className={
            shouldUseDesktopView
              ? 'mx-auto w-full max-w-md p-4 text-center shadow-sm xl:col-span-2'
              : ''
          }
        >
          <TopicMore />
        </div>
      </div>
    </div>
  )

  if (shouldUseDesktopView) {
    return (
      <>
        <Head>
          <title>Pelatihan Mandiri | Untuk Anda</title>
        </Head>
        <DesktopLandingShell activeMenu="untuk-anda">
          {content}
        </DesktopLandingShell>
      </>
    )
  }

  return (
    <>
      <Head>
        <title>Pelatihan Mandiri | Untuk Anda</title>
      </Head>
      <TabbedNav activeMenu="untuk-anda" scrollClicked={handleScrollClicked} />
      <div
        className="bg-gray-10"
        style={{
          minHeight: 'calc(100vh - 3.5rem)',
        }}
      >
        {content}
      </div>
    </>
  )
}

export default ForYouRevamp
